import { distinctNonAscii, normalizeCheck, type UnicodeCheck, type WontCompileChar } from "./pasteFixModel";

export type CheckChars = (chars: string) => Promise<UnicodeCheck>;

/**
 * Asks the server about each character once. A document is checked on load and
 * again as it changes, and a paste mostly repeats characters already seen, so
 * without this every keystroke after a Greek letter would be a round trip.
 *
 * Only a real answer is remembered. `checked: false` (catalog not loaded) or a
 * failed request caches nothing, so the next check asks again rather than
 * treating the text as clean forever.
 */
export function createCharClassifier(check: CheckChars) {
  /** char → the won't-compile entry, or null when it compiles. */
  const known = new Map<string, WontCompileChar | null>();

  return {
    /** The won't-compile characters in `text`, or null when it could not be checked. */
    async wontCompileIn(text: string): Promise<WontCompileChar[] | null> {
      const chars = [...distinctNonAscii(text)];
      const unknown = chars.filter((c) => !known.has(c));

      if (unknown.length > 0) {
        let result: UnicodeCheck;
        try {
          result = await check(unknown.join(""));
        } catch {
          return null;
        }
        if (!result.checked) return null;
        for (const c of unknown) known.set(c, null);
        for (const w of result.wontCompile) known.set(w.char, w);
      }

      return chars.map((c) => known.get(c)).filter((w): w is WontCompileChar => !!w);
    },
  };
}

export function checkCharsWith(fetchWithAuth: (url: string, init?: RequestInit) => Promise<Response>): CheckChars {
  return async (chars) => {
    const res = await fetchWithAuth("/api/unicode/check", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: chars }),
    });
    if (!res.ok) throw new Error(`unicode check ${res.status}`);
    return normalizeCheck(await res.json());
  };
}
